'use client';

import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

type Day = { date: string; status: 'up' | 'partial' | 'down'; uptimePct: number; hasData: boolean };
type NodeStatus = { id: string; name: string; region: string; online: boolean; uptimePct: number; history: Day[] };

const REGION_LABEL_KEYS: Record<string, string> = { NA: 'regionNA', EU: 'regionEU' };
const REGION_ORDER = ['NA', 'EU'];

const BAR_COLORS: Record<Day['status'], string> = {
  up: 'bg-emerald-500',
  partial: 'bg-amber-500',
  down: 'bg-red-500',
};

function formatPct(value: number) {
  // Never round a day with any downtime up to 100.00%
  if (value < 100 && value > 99.995) return '99.99%';
  return `${value.toFixed(2)}%`;
}

function formatDay(date: string) {
  return new Date(`${date}T00:00:00Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function UptimeBars({ history }: { history: Day[] }) {
  const t = useTranslations('Status');
  return (
    <div className="flex h-9 w-full items-stretch gap-[3px]" role="img" aria-label={t('uptimeAria')}>
      {history.map((day) => (
        <div key={day.date} className="group relative flex-1">
          <div className={cn('h-full w-full rounded-[3px] transition-opacity group-hover:opacity-80', BAR_COLORS[day.status])} />
          <div className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 hidden -translate-x-1/2 whitespace-nowrap rounded-full border border-border bg-popover px-3 py-1 text-xs text-popover-foreground shadow-md group-hover:block">
            {formatDay(day.date)} · {day.hasData ? formatPct(day.uptimePct) : '100.00%'}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function StatusPage() {
  const t = useTranslations('Status');
  const [nodes, setNodes] = useState<NodeStatus[] | null>(null);
  const [error, setError] = useState('');
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await fetch('/api/status', { cache: 'no-store' });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || t('loadFailed'));
        if (cancelled) return;
        setNodes(data.nodes || []);
        setUpdatedAt(new Date(data.updatedAt));
        setError('');
      } catch (err: any) {
        if (!cancelled) setError(err?.message || t('loadFailed'));
      }
    }
    load();
    const id = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const groups = useMemo(() => {
    const map = new Map<string, NodeStatus[]>();
    for (const node of nodes || []) {
      const key = node.region || 'NA';
      map.set(key, [...(map.get(key) || []), node]);
    }
    return Array.from(map.entries()).sort(([a], [b]) => {
      const ia = REGION_ORDER.indexOf(a);
      const ib = REGION_ORDER.indexOf(b);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });
  }, [nodes]);

  const downCount = (nodes || []).filter((n) => !n.online).length;
  const total = (nodes || []).length;
  const banner =
    total === 0 || downCount === 0
      ? { text: t('allOperational'), cls: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-500' }
      : downCount === total
        ? { text: t('majorOutage'), cls: 'border-red-500/40 bg-red-500/10 text-red-500' }
        : { text: t('nodesOffline', { down: downCount, total }), cls: 'border-amber-500/40 bg-amber-500/10 text-amber-500' };

  return (
    <div className="mx-auto w-full max-w-5xl px-4 pb-20 pt-28 sm:px-6">
      <h1 className="text-3xl font-semibold tracking-tight">{t('title')}</h1>
      <p className="mt-1 text-sm text-muted-foreground">{t('subtitle')}</p>

      {!nodes && !error && (
        <div className="mt-16 flex justify-center text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
      )}
      {error && !nodes && (
        <p className="mt-8 rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">{error}</p>
      )}

      {nodes && (
        <>
          <div className={cn('mt-6 rounded-lg border px-4 py-3 text-sm font-semibold', banner.cls)}>{banner.text}</div>
          <div className="my-8 h-px bg-border" />

          <div className="space-y-8">
            {groups.map(([region, regionNodes]) => (
              <section key={region}>
                <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {REGION_LABEL_KEYS[region] ? t(REGION_LABEL_KEYS[region]) : region}
                </h2>
                <div className="rounded-xl border border-border bg-card">
                  {regionNodes.map((node, i) => (
                    <div
                      key={node.id}
                      className={cn(
                        'grid grid-cols-[1fr_auto] items-center gap-x-6 gap-y-3 px-5 py-4 md:grid-cols-[180px_1fr_96px]',
                        i > 0 && 'border-t border-border',
                      )}
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold">{node.name}</p>
                        <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                          <span className={cn('h-2 w-2 rounded-full', node.online ? 'bg-emerald-500' : 'bg-red-500')} />
                          {node.online ? t('operational') : t('offline')}
                        </p>
                      </div>
                      <div className="col-span-2 row-start-2 md:col-span-1 md:row-start-auto">
                        <UptimeBars history={node.history} />
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold tabular-nums">{formatPct(node.uptimePct)}</p>
                        <p className="mt-1 text-xs text-muted-foreground">{t('days14')}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
            {groups.length === 0 && <p className="text-sm text-muted-foreground">{t('noNodes')}</p>}
          </div>

          {updatedAt && (
            <p className="mt-8 text-xs text-muted-foreground">{t('lastUpdated', { time: updatedAt.toLocaleTimeString() })}</p>
          )}
        </>
      )}
    </div>
  );
}
